import { Button, Modal, Space, Switch } from "antd";
import type { Category } from "api/config/chalice-types";
import React from "react";

// Type for the combined data prop
// Export the type
export interface CategoryDeleteData {
  category: Category;
  topicCount: number;
}

interface CategoryDeleteConfirmModalProps {
  categoryData: CategoryDeleteData | null; // Expect combined data
  isOpen: boolean;
  onClose: () => void;
  onConfirmDeleteAndMove: (category: Category) => Promise<void>; // Action for moving topics
  onConfirmDeleteAll: (category: Category) => Promise<void>; // Action for deleting topics
}

const CategoryDeleteConfirmModal: React.FC<CategoryDeleteConfirmModalProps> = ({
  categoryData,
  isOpen,
  onClose,
  onConfirmDeleteAndMove,
  onConfirmDeleteAll,
}) => {
  // State for the switch
  const [deleteTopicsToo, setDeleteTopicsToo] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  // Reset switch state when modal opens/category changes
  React.useEffect(() => {
    if (isOpen) {
      setDeleteTopicsToo(false);
    }
  }, [isOpen]);

  if (!categoryData) {
    return null; // Don't render if no data is provided
  }

  const { category, topicCount } = categoryData;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      if (deleteTopicsToo) {
        await onConfirmDeleteAll(category);
      } else {
        await onConfirmDeleteAndMove(category);
      }
    } finally {
      setIsLoading(false);
      onClose(); // Close modal regardless of success/failure
    }
  };

  return (
    <Modal
      title={`Delete Category "${category.name}"`}
      open={isOpen}
      onCancel={onClose}
      // Use a single confirmation button
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>,
        <Button
          key="confirm-delete"
          type="primary"
          danger
          loading={isLoading}
          onClick={handleConfirm}
        >
          {/* Dynamically change button text based on switch state */}
          {deleteTopicsToo
            ? `Delete Category and ${topicCount} Topics`
            : "Delete Category"}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <p className="text-[var(--antd-colorTextSecondary)]">
          Are you sure you want to delete the category?{" "}
          <span
            className={`transition-all duration-200 ${deleteTopicsToo ? "line-through opacity-60" : "opacity-100"}`}
          >
            All topics within this category will be moved to "Uncategorized".
          </span>
        </p>
        <Space direction="horizontal" align="center">
          <Switch
            checked={deleteTopicsToo}
            onChange={setDeleteTopicsToo}
            disabled={isLoading}
          />
          <span
            className={`transition-opacity duration-200 ${deleteTopicsToo ? "opacity-100" : "opacity-50"} text-[var(--antd-colorTextSecondary)]`}
          >
            Also delete all ({topicCount}) topics within this category.
          </span>
        </Space>
      </Space>
    </Modal>
  );
};

export default CategoryDeleteConfirmModal;
